//@ts-nocheck
import { format as formatDateFn } from 'date-fns';

/**
 * Travel Date Rules
 *
 * Public-facing travel dates are calendar dates, not timestamps.
 *
 * Use TravelDateString (YYYY-MM-DD) for:
 * - trip dates
 * - itinerary dates
 * - LineItem travel dates
 * - check-in/check-out dates
 *
 * Do not use TravelDateString for:
 * - created/updated timestamps
 * - audit/event times
 * - payment timestamps
 *
 * Required pattern:
 * - normalize incoming travel dates
 * - calculate with travel-date helpers
 * - display with travel-date formatting helpers
 *
 * In scope:
 * - public-facing output templates
 * - PWA/final itinerary
 * - travel docs
 * - public-facing LineItem travel dates
 *
 * Out of scope:
 * - admin/internal timestamps
 * - user-entered date input behavior
 * - backend storage changes
 * - legacy/deprecated TripDetailedDay / TripDetailedDayService workflows
 */

const TRAVEL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (value) => String(value).padStart(2, '0');

const getPartsFromTravelDate = (travelDate) => {
  if (typeof travelDate !== 'string') {
    return null;
  }

  const match = travelDate.trim().match(TRAVEL_DATE_PATTERN);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if ([year, month, day].some((value) => Number.isNaN(value))) {
    return null;
  }

  const utcDate = new Date(Date.UTC(year, month - 1, day));
  if (
    utcDate.getUTCFullYear() !== year ||
    utcDate.getUTCMonth() !== month - 1 ||
    utcDate.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
};

const toTravelDateString = (year, month, day) =>
  `${year}-${pad(month)}-${pad(day)}`;

const toUtcDateFromTravelDate = (travelDate) => {
  const parts = getPartsFromTravelDate(travelDate);
  if (!parts) {
    return null;
  }

  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
};

const toLocalDisplayDate = (travelDate) => {
  const parts = getPartsFromTravelDate(travelDate);
  if (!parts) {
    return null;
  }

  return new Date(parts.year, parts.month - 1, parts.day, 12, 0, 0, 0);
};

/**
 * @typedef {string} TravelDateString
 * Canonical format: YYYY-MM-DD
 * Timezone-agnostic calendar date for public-facing travel-domain dates.
 */

export const normalizeTravelDate = (input) => {
  if (!input) {
    return null;
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    const direct = trimmed.match(TRAVEL_DATE_PATTERN);
    if (direct) {
      const normalized = toTravelDateString(
        Number(direct[1]),
        Number(direct[2]),
        Number(direct[3])
      );
      return getPartsFromTravelDate(normalized) ? normalized : null;
    }

    const isoLike = trimmed.match(/^(\d{4}-\d{2}-\d{2})T/);
    if (isoLike) {
      return getPartsFromTravelDate(isoLike[1]) ? isoLike[1] : null;
    }

    return null;
  }

  if (input instanceof Date && !Number.isNaN(input.getTime())) {
    return toTravelDateString(
      input.getFullYear(),
      input.getMonth() + 1,
      input.getDate()
    );
  }

  return null;
};

export const isTravelDateString = (value) => {
  return Boolean(getPartsFromTravelDate(value));
};

export const addTravelDays = (travelDate, daysToAdd = 0) => {
  const normalizedTravelDate = normalizeTravelDate(travelDate);
  const utcDate = toUtcDateFromTravelDate(normalizedTravelDate);
  const numericDaysToAdd = Number(daysToAdd);

  if (!utcDate || Number.isNaN(numericDaysToAdd)) {
    return null;
  }

  utcDate.setUTCDate(utcDate.getUTCDate() + numericDaysToAdd);

  return toTravelDateString(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth() + 1,
    utcDate.getUTCDate()
  );
};

export const formatTravelDate = (travelDate, formatPattern = 'dd MMM yyyy') => {
  const normalizedTravelDate = normalizeTravelDate(travelDate);
  const localDisplayDate = toLocalDisplayDate(normalizedTravelDate);

  if (!localDisplayDate) {
    return '';
  }

  return formatDateFn(localDisplayDate, formatPattern);
};

export const getTravelDayOfWeekName = (travelDate, formatPattern = 'EEEE') => {
  return formatTravelDate(travelDate, formatPattern);
};
